import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Spin, Switch } from 'antd';
import * as claus from 'src/_shared/services/claus/endpoints';
import { buildPatchPayload, recordToFieldMappingRows, statusMapToEntries, validateSchedule } from 'src/_shared/services/claus/serde';
import JobSyncConfigComponent from '../job-sync/JobSyncConfigComponent.jsx';
import CandidateSyncConfigComponent from '../candidate-sync/CandidateSyncConfigComponent.jsx';
import ConsoleHeader from '../console/ConsoleHeader.jsx';
import SaveBar from '../console/SaveBar.jsx';
import Section from '../console/Section.jsx';
import { useAsync, useIntegration } from './hooks/useClaus';
import BasicSection from './sections/BasicSection.jsx';
import CredentialsSection from './sections/CredentialsSection.jsx';
import InputParamsSection from './sections/InputParamsSection.jsx';
import StatusMappingsSection from './sections/StatusMappingsSection.jsx';
import ScheduleSection from './sections/ScheduleSection.jsx';
import WebhooksSection from './sections/WebhooksSection.jsx';
import SftpCredentialsSection from './sections/SftpCredentialsSection.jsx';
import SyncSection from './sections/SyncSection.jsx';

// Turn a raw schema key ('api_key', 'username') into a human label ('API key', 'Username'), preserving
// common acronyms that naive title-casing would mangle (API / URL / SFTP / ID …).
const ACRONYMS = { api: 'API', url: 'URL', sftp: 'SFTP', ssh: 'SSH', id: 'ID', oauth: 'OAuth', ats: 'ATS' };
function humanizeKey(key) {
	if (!key) return key;
	const words = String(key).replace(/([a-z])([A-Z])/g, '$1 $2').split(/[_\s]+/);
	return words
		.map((w, i) => {
			const lower = w.toLowerCase();
			if (ACRONYMS[lower]) return ACRONYMS[lower];
			return i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : lower;
		})
		.join(' ');
}

export default function IntegrationEditor({ orgId, id, currentUser, company, tieredBonuses, onDirtyChange, onBack }) {
	const { loading, data: integration, error, reload } = useIntegration(orgId, id);
	// Webhooks + sync history load at the editor level and are handed down to their sections.
	const hooks = useAsync(() => claus.listWebhooks(orgId, id), [orgId, id]);
	const history = useAsync(() => claus.getSyncHistory(orgId, id, { page: 1, limit: 10 }), [orgId, id]);

	const [tab, setTab] = useState('integration');
	const [form, setForm] = useState(null);
	const [saving, setSaving] = useState(false);
	const [notice, setNotice] = useState(null);
	const [processingDirty, setProcessingDirty] = useState(false);
	// Bumped whenever `form` is (re)seeded from outside an edit — load, save-reload, Reset — so
	// sections that hold local row state (InputParamsSection) remount in sync.
	const [formEpoch, setFormEpoch] = useState(0);
	// The Processing tab's mode, mirrored up so the page header can render the Dry-run/Live toggle;
	// actions ref lets the header invoke the child's confirm-gated mode switch.
	const [procMode, setProcMode] = useState(null);
	const procActionsRef = useRef({});
	const initialFormRef = useRef(null);

	// Initialise the editable form from the loaded integration (credentials stay blank = keep).
	useEffect(() => {
		if (!integration) return;
		const initForm = {
			name: integration.name || '',
			enabled: !!integration.enabled,
			authParams: {},
			inputParams: { ...(integration.inputParams || {}) },
			fieldMappingRows: recordToFieldMappingRows(integration.fieldMappings || {}),
			statusEntries: statusMapToEntries(integration.statusMappings),
			schedule: integration.schedule || { frequency: 1, unit: 'hour' },
		};
		setForm(initForm);
		initialFormRef.current = JSON.stringify(initForm);
		setFormEpoch((e) => e + 1);
		setNotice(null);
	}, [integration]);

	// The GET detail returns masked authParams as an array — use its keys to drive the credential
	// inputs on edit (no template fetch needed).
	const credTemplate = useMemo(() => {
		const keys = (integration?.authParams || []).map((p) => p.type).filter(Boolean);
		return {
			authSchemaV2: { requiredFields: keys.map((k) => ({ key: k, label: humanizeKey(k) })), optionalFields: [] },
		};
	}, [integration]);

	const isCandidate = integration?.type === 'candidate';
	const isSftp = integration?.source === 'sftp';
	const patch = (partial) => setForm((f) => ({ ...f, ...partial }));

	// dirty tracking so the source save bar mirrors the Processing tab and the host can guard navigation.
	const sourceDirty =
		!!form && initialFormRef.current !== null && JSON.stringify(form) !== initialFormRef.current;
	const anyDirty = sourceDirty || processingDirty;
	useEffect(() => {
		if (onDirtyChange) onDirtyChange(anyDirty);
	}, [anyDirty, onDirtyChange]);

	function resetForm() {
		if (initialFormRef.current) {
			setForm(JSON.parse(initialFormRef.current));
			setFormEpoch((e) => e + 1);
		}
		setNotice(null);
	}

	async function save() {
		if (!form.name.trim()) {
			setNotice({ type: 'error', message: 'Give the integration a name.' });
			return;
		}
		if (!isSftp) {
			const err = validateSchedule(form.schedule);
			if (err) {
				setNotice({ type: 'error', message: err });
				return;
			}
		}
		setSaving(true);
		setNotice(null);
		try {
			const payload = buildPatchPayload(form, { type: integration.type, source: integration.source });
			await claus.patchIntegration(orgId, id, payload);
			setNotice({ type: 'success', message: 'Integration saved.' });
			await reload();
		} catch (e) {
			setNotice({ type: 'error', message: e.message || 'Could not save the integration.' });
		} finally {
			setSaving(false);
		}
	}

	if (loading) {
		return (
			<div className="js-loading">
				<Spin />
			</div>
		);
	}
	if (error) {
		return <Alert type="error" showIcon message="Could not load the integration" description={error.message} />;
	}
	if (!integration || !form) return null;

	const headerRight =
		tab === 'integration' ? (
			<>
				<span className="ei-headlabel">Enabled</span>
				<Switch checked={!!form.enabled} onChange={(v) => patch({ enabled: v })} />
			</>
		) : tab === 'processing' && procMode ? (
			<div className={`js-modetoggle${procMode === 'live' ? ' is-live' : ''}`} role="radiogroup" aria-label="Sync mode">
				<button
					type="button"
					role="radio"
					aria-checked={procMode !== 'live'}
					className={`js-modeopt${procMode !== 'live' ? ' is-selected' : ''}`}
					onClick={() => procActionsRef.current.requestMode && procActionsRef.current.requestMode('dry-run')}
				>
					Dry-run
				</button>
				<button
					type="button"
					role="radio"
					aria-checked={procMode === 'live'}
					className={`js-modeopt${procMode === 'live' ? ' is-selected' : ''}`}
					onClick={() => procActionsRef.current.requestMode && procActionsRef.current.requestMode('live')}
				>
					<span className="js-modeopt-dot" aria-hidden />
					Live
				</button>
			</div>
		) : null;

	return (
		<>
			<ConsoleHeader
				onBack={onBack}
				title={integration.name}
				subtitle={`${integration.type} · ${integration.source}`}
				rightSlot={headerRight}
			/>

			<div className="ei-tabs" role="tablist" aria-label="Editor sections">
				<button
					type="button"
					id="tab-integration"
					role="tab"
					aria-selected={tab === 'integration'}
					className={`ei-tab${tab === 'integration' ? ' is-selected' : ''}`}
					onClick={() => setTab('integration')}
				>
					Integration (source)
				</button>
				<button
					type="button"
					id="tab-processing"
					role="tab"
					aria-selected={tab === 'processing'}
					className={`ei-tab${tab === 'processing' ? ' is-selected' : ''}`}
					onClick={() => setTab('processing')}
				>
					Processing
				</button>
			</div>

			{/* Every panel below stays mounted (the inactive ones hidden) so switching tabs never discards
			    unsaved edits. */}
			<div role="tabpanel" aria-labelledby="tab-integration" hidden={tab !== 'integration'}>
				<Section id="sec-basic" title="Overview" description="Name and template for this integration.">
					<BasicSection
						name={form.name}
						onChangeName={(v) => patch({ name: v })}
						templateName={integration.templateName}
						type={integration.type}
						source={integration.source}
					/>
				</Section>

				{!isSftp && (
					<Section id="sec-cred" title="Credentials" description="Leave a field blank to keep its current value.">
						<CredentialsSection
							template={credTemplate}
							authParams={form.authParams}
							onChange={(a) => patch({ authParams: a })}
							isEdit
						/>
					</Section>
				)}

				<Section id="sec-input" title="Input parameters" description="Extra values passed to the source on each fetch.">
					<InputParamsSection
						key={`params-${formEpoch}`}
						template={null}
						inputParams={form.inputParams}
						onChange={(p) => patch({ inputParams: p })}
					/>
				</Section>

				{isCandidate && (
					<Section
						id="sec-status"
						title="Status mappings"
						description="Map incoming candidate statuses to ERIN statuses and stages."
					>
						<StatusMappingsSection entries={form.statusEntries} onChange={(e) => patch({ statusEntries: e })} />
					</Section>
				)}

				{!isSftp && (
					<Section
						id="sec-schedule"
						title="Sync schedule"
						description={`How often ERIN pulls new and changed ${isCandidate ? 'candidates' : 'jobs'} from the source.`}
					>
						<ScheduleSection schedule={form.schedule} onChange={(s) => patch({ schedule: s })} showLookback />
					</Section>
				)}

				<WebhooksSection orgId={orgId} integrationId={id} hooks={hooks} />

				{isSftp && <SftpCredentialsSection orgId={orgId} />}

				<SyncSection
					orgId={orgId}
					integrationId={id}
					source={integration.source}
					history={history}
					title={isCandidate ? 'Fetch activity (upstream ATS pull)' : undefined}
				/>

				{notice && (
					<Alert
						style={{ marginTop: 14 }}
						type={notice.type}
						showIcon
						closable
						message={notice.message}
						onClose={() => setNotice(null)}
					/>
				)}

				<SaveBar
					tone={sourceDirty ? 'dirty' : 'clean'}
					statusText={sourceDirty ? 'Unsaved changes' : 'All changes saved'}
					sub="Changes to credentials, params, mappings and schedule save here."
					onReset={resetForm}
					onSave={save}
					resetDisabled={!sourceDirty}
					saveDisabled={!sourceDirty}
					saving={saving}
				/>
			</div>

			{/* Reused components — each self-loads/saves its config on the Company record via AppSync.
			    Job integrations edit jobSyncConfig; candidate integrations edit its candidateSyncConfig
			    sibling. Always embedded here; the Dry-run/Live toggle renders in this editor's page
			    header via the mode bridge (onModeState + modeActionsRef). */}
			<div role="tabpanel" aria-labelledby="tab-processing" hidden={tab !== 'processing'}>
				{isCandidate ? (
					<CandidateSyncConfigComponent
						onDirtyChange={setProcessingDirty}
						onModeState={setProcMode}
						modeActionsRef={procActionsRef}
						currentUser={currentUser}
						company={company}
						tieredBonuses={tieredBonuses}
					/>
				) : (
					<JobSyncConfigComponent
						onDirtyChange={setProcessingDirty}
						onModeState={setProcMode}
						modeActionsRef={procActionsRef}
						currentUser={currentUser}
						company={company}
						tieredBonuses={tieredBonuses}
					/>
				)}
			</div>
		</>
	);
}
